import * as THREE from 'three'

function createArrowGeometry({ length, height, head, depth })
{
    const half = height / 2
    const neck = length / 2 - head
    const tail = - length / 2
    const tip = length / 2
    const shape = new THREE.Shape()

    shape.moveTo(tail, - half)
    shape.lineTo(neck, - half)
    shape.lineTo(tip, 0)
    shape.lineTo(neck, half)
    shape.lineTo(tail, half)
    shape.closePath()

    const geometry = new THREE.ExtrudeGeometry(shape, { depth, bevelEnabled: false })
    geometry.translate(0, 0, - depth / 2)
    geometry.rotateX(Math.PI / 2)
    geometry.computeVertexNormals()

    return geometry
}

function createLabelMaterial(_label, _mirror, _flipY)
{
    const width = 1024
    const height = 256
    const canvas = document.createElement('canvas')
    canvas.width = width
    canvas.height = height
    const context = canvas.getContext('2d')
    context.clearRect(0, 0, width, height)
    context.fillStyle = '#8a8a8a'
    context.textAlign = 'center'
    context.textBaseline = 'middle'
    const fontSize = _label.length > 9 ? 132 : 176
    context.font = `700 ${fontSize}px "Arial Black", Arial, sans-serif`
    context.fillText(_label.toUpperCase(), width / 2, height / 2 + 6)

    if(_mirror)
    {
        const source = context.getImageData(0, 0, width, height)
        const flipped = context.createImageData(width, height)

        for(let y = 0; y < height; y++)
        {
            for(let x = 0; x < width; x++)
            {
                const sourceIndex = (y * width + (width - 1 - x)) * 4
                const targetIndex = (y * width + x) * 4
                flipped.data[targetIndex] = source.data[sourceIndex]
                flipped.data[targetIndex + 1] = source.data[sourceIndex + 1]
                flipped.data[targetIndex + 2] = source.data[sourceIndex + 2]
                flipped.data[targetIndex + 3] = source.data[sourceIndex + 3]
            }
        }

        context.putImageData(flipped, 0, 0)
    }

    const texture = new THREE.CanvasTexture(canvas)
    texture.colorSpace = THREE.SRGBColorSpace
    texture.flipY = _flipY
    texture.magFilter = THREE.LinearFilter
    texture.minFilter = THREE.LinearFilter
    texture.needsUpdate = true

    return new THREE.MeshBasicMaterial({
        map: texture,
        transparent: true,
        depthWrite: true,
        side: THREE.FrontSide,
    })
}

export function createDirectionSign({ label, materials, position, pointAt, reverse = false })
{
    const longLabel = label.length > 9
    const length = longLabel ? 4.7 : 3.25
    const height = 0.82
    const head = 0.92
    const boardDepth = 0.22
    const sign = new THREE.Group()
    sign.position.copy(position)
    sign.rotation.z = Math.atan2(pointAt.y - position.y, pointAt.x - position.x) + (reverse ? Math.PI : 0)

    const post = new THREE.Mesh(
        new THREE.BoxGeometry(0.1, 0.1, 3.05),
        materials.shades.items.brown,
    )
    post.position.z = 1.52
    sign.add(post)

    const board = new THREE.Mesh(
        createArrowGeometry({ length, height, head, depth: boardDepth }),
        materials.shades.items.white,
    )
    board.position.set(length * 0.12, 0, 3.12)
    sign.add(board)

    const labelWidth = length - head - 0.35
    const labelGeometry = new THREE.PlaneGeometry(labelWidth, height * 0.55)
    const labelX = board.position.x - head * 0.55

    for(const side of [1, - 1])
    {
        const labelMesh = new THREE.Mesh(labelGeometry, createLabelMaterial(label, true, false))
        labelMesh.position.set(labelX, side * (boardDepth / 2 + 0.012), board.position.z)
        labelMesh.rotation.x = - Math.PI / 2

        if(side === - 1)
        {
            labelMesh.rotation.y = Math.PI
        }

        sign.add(labelMesh)
    }

    const pebbleGeometry = new THREE.DodecahedronGeometry(0.12, 0)
    const pebbleOffsets = [
        [0.28, 0.22],
        [- 0.18, - 0.26],
        [0.16, - 0.2],
    ]

    for(const [x, y] of pebbleOffsets)
    {
        const pebble = new THREE.Mesh(pebbleGeometry, materials.shades.items.white)
        pebble.position.set(x, y, 0.08)
        pebble.rotation.set(0.4, 0.2, 0.6)
        sign.add(pebble)
    }

    sign.traverse((child) =>
    {
        child.matrixAutoUpdate = false
        child.updateMatrix()
    })
    sign.updateMatrix()

    return sign
}

export function createSignPostCollision(_position)
{
    const collision = new THREE.Object3D()
    const post = new THREE.Mesh(new THREE.BoxGeometry(1, 1, 1))
    post.name = 'cube'
    post.position.set(_position.x, _position.y, 1.6)
    post.scale.set(0.22, 0.22, 3.2)
    collision.add(post)

    return collision
}
